import React, { useState, useEffect, useRef, useContext } from 'react';
import './CorporationDetailModal.css';
import Modal from '../Modal/Modal';
import WebLinkList from '../ProcessChanges/WebLinkList'
import OrganizationHistory from '../OrganizationHistory/OrganizationHistory';
import FacilityLogo from '../FacilityLogo/FacilityLogo';
import TruncateText from '../TruncateText/TruncateText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ActionButtons from '../ActionButtons/ActionButtons';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import convenienceApi from '../../common/api/convenience';
import logComponent from '../../common/logComponent';

export default function CorporationDetailModal({ corporation, changeOrganization, updateCorporation, hide }) {
  const environmentContext = useContext(EnvironmentContext);
  const { activeUser: user } = environmentContext;
  const [logoToUpload, setLogoToUpload] = useState(null);
  const [status, setStatus] = useState('initial');
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [weblinks, setWeblinks] = useState(null);
  const [weblinksLoading, setWeblinksLoading] = useState(null);
  const [weblinksError, setWeblinksError] = useState(null);
  const [logoErrorResponse, setLogoErrorResponse] = useState(null);
  const modalRef = useRef(null);

  if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal rendered'); }


  const setModalRef = (el) => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal setModalRef'); }
    modalRef.current = el;
  }
  const actionButtons = useRef(null);


  useEffect(() => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal onMount'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { hasToken } = environmentContext;
    if (!hasToken) return;
  }, []);

  useEffect(() => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal useEffect [environmentContext.hasToken]'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;
    loadWeblinks();

  }, [environmentContext.hasToken]);


  const loadWeblinks = () => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal loadWeblinks'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;

    setWeblinks(null);
    setWeblinksLoading(true);
    setWeblinksError(false);

    convenienceApi.getWeblinksForOrganization(corporation)
      .then(
        result => {
          setWeblinks(result);
          setWeblinksLoading(false);
          setWeblinksError(false);
        },
        error => {
          setWeblinks(null);
          setWeblinksLoading(false);
          setWeblinksError(true);
        });
  }

  const handleFacilityClick = (e) => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal handleFacilityClick'); }
    const { facilityId } = e.currentTarget.dataset;

    // get facility based on id
    // NOTE: we want == in this case b/c the facilityId coming from the
    // dataset is a string, but the value in the linkedFacility is a number
    const targetFacility = corporation.uiData.linkedFacilities.find(facility =>
      facility.salesforceAccountId &&
      facility.salesforceAccountId == facilityId); // eslint-disable-line

    changeOrganization(targetFacility);
  }

  const setNewLogo = (logo) => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal setNewLogo'); }
    setLogoToUpload(logo);
    setStatus('initial');

  }

  const submit = () => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal submit'); }

    if (!logoToUpload) {
      startModalExit();
    } else {
      setStatus('pending');

      convenienceApi.uploadLogo(corporation, logoToUpload)
        .then(
          result => {
            setStatus('success');
            updateCorporation(result);
            startModalExit();
          },
          error => {
            setLogoErrorResponse(error.response.data);
            setStatus('failure');
            modalRef.current.scrollTop = 0;
          }
        )
    }
  }

  const startModalExit = () => {
    if (logComponent('CorporationDetailModal')) { console.debug('CorporationDetailModal startModalExit'); }
    setModalShouldExit(true);
  }


  return (
    <Modal isVisible={true} shouldExit={modalShouldExit} hide={hide} ref={setModalRef} remainOnBackdropClick={true}>
      <div className="Corporation-Detail-Modal">
        <h4 className="Corporation-Detail-Modal__heading">{corporation.name}</h4>
        <p className={"Corporation-Detail-Modal__page-error" + (status === 'failure' ? ' visible' : '')} >{logoErrorResponse ? logoErrorResponse : 'Sorry, something went wrong.  Changes were not saved.  Please try again.'}</p>
        <dl className="Corporation-Detail-Modal__list">
          {
            user.isAdmin &&
            <React.Fragment>
              <dt>Corporation Logo</dt>
              <dd>
                <FacilityLogo
                  facility={corporation}
                  onChange={setNewLogo} />
              </dd>
            </React.Fragment>
          }
          <dt>Status</dt>
          <dd>Active {/* TODO: Replace with dynamic data once it's available */}</dd>
          <dt>About Us</dt>
          <dd>
            {!corporation.aboutUs ?
              <span className="Corporation-Detail-Modal__empty-field">Not Provided</span> :
              <TruncateText text={corporation.aboutUs} truncateAt={100} />
            }
          </dd>
          <dt>Postal Code</dt>
          <dd>
            {corporation.postalCode ||
              <span className="Corporation-Detail-Modal__empty-field">Not Provided</span>
            }
          </dd>
          <dt>Industry Type</dt>
          <dd>
            {corporation.industryType ||
              <span className="Corporation-Detail-Modal__empty-field">Not Provided</span>
            }
          </dd>
          <dt>Web Links</dt>
          <dd>
            {
              weblinksLoading ?
                <p className="Corporation-Detail-Modal__weblinks-loading">Loading web links...</p> :
                weblinksError ?
                  <p className="Corporation-Detail-Modal__weblinks-error">Failed to load web links.
                    <button
                      className="Corporation-Detail-Modal__reload-weblinks"
                      onClick={loadWeblinks}>
                      Try Again
                      <FontAwesomeIcon icon="redo-alt" />
                    </button>
                  </p> :
                  (!weblinks || weblinks.length === 0) ?
                    <span className="Corporation-Detail-Modal__empty-field">Not Provided</span> :
                    <WebLinkList links={weblinks} />
            }
          </dd>
          <dt>Facility Relationships</dt>
          <dd>
            {
              corporation.uiData.linkedFacilities.length === 0 ?
                <span className="Corporation-Detail-Modal__empty-field">No Facility Relationships</span> :
                <ul className="Corporation-Detail-Modal__linked-facilities">
                  {
                    corporation.uiData.linkedFacilities.map(facility =>
                      <li
                        className="Corporation-Detail-Modal__facility-item"
                        key={facility.salesforceAccountId}>
                        <span className="Corporation-Detail-Modal__facility-id">{facility.salesforceSiteId}</span>
                        <span className="Corporation-Detail-Modal__facility-name">
                          {facility.name}
                          {
                            (!facility.status || facility.status !== 'rejected') && user.isAdmin &&
                            <button
                              className="Corporation-Detail-Modal__facility-link"
                              data-facility-id={facility.salesforceAccountId}
                              onClick={handleFacilityClick}>
                              <FontAwesomeIcon icon="external-link-alt" />
                            </button>
                          }
                        </span>
                      </li>
                    )
                  }
                </ul>
            }
          </dd>
          <dt>History</dt>
          <dd>
            <OrganizationHistory organization={corporation} />
          </dd>
        </dl>
        {
          user.isAdmin ?
            <ActionButtons
              ref={actionButtons}
              mainAction={submit}
              mainActionText="Save changes"
              cancel={startModalExit}
              cancelText="Cancel"
              currentStatus={status} /> :
            <ActionButtons
              ref={actionButtons}
              cancel={startModalExit}
              cancelText="Close" />
        }
      </div>
    </Modal>
  );
}